/**
 * Quote Builder — money helpers.
 *
 * All API prices are integer *minor* units (kuruş / cent). The UI works
 * in *major* units and delegates the ×100 / ÷100 conversion to these
 * helpers so the same rules are applied everywhere.
 */

/** Minor units → number (major). Returns 0 on non-numeric input. */
export function minorToMajor(minor) {
  const n = Number(minor);
  if (!Number.isFinite(n)) return 0;
  return n / 100;
}

/** Major number/string → minor integer. Anything that can't be parsed → 0. */
export function majorToMinor(major) {
  if (major === '' || major === null || major === undefined) return 0;
  const s = String(major).replace(',', '.').trim();
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return 0;
  // Round HALF_UP to avoid floating point drift on values like 12.34
  return Math.round(n * 100);
}

/** Format minor units as Turkish currency string. */
export function formatMinor(minor, currency = 'TRY', locale = 'tr-TR') {
  const value = minorToMajor(minor);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency}`;
  }
}

/** Format a plain number with 2 decimals (used for line totals in inputs). */
export function formatMajor(major, locale = 'tr-TR') {
  const n = Number(major);
  if (!Number.isFinite(n)) return '0,00';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

/**
 * Client-side subtotal for a preview while the user is editing.
 * The authoritative value is always the one returned by the backend
 * after the quote is saved.
 */
export function computeClientSubtotalMinor(items = []) {
  let total = 0;
  for (const it of items) {
    const qty = Number(String(it.quantity || 0).replace(',', '.'));
    const price = Number(it.unit_price_minor || 0);
    if (!Number.isFinite(qty) || !Number.isFinite(price)) continue;
    total += Math.round(qty * price);
  }
  return total;
}

// ISO 4217 codes accepted by the backend (services/quote_regional.py).
// A free-text field let typos through, which broke money formatting.
export const QUOTE_CURRENCIES = [
  ['TRY', 'TRY · ₺'], ['USD', 'USD · $'], ['EUR', 'EUR · €'], ['GBP', 'GBP · £'],
  ['CHF', 'CHF'], ['CAD', 'CAD · C$'], ['AUD', 'AUD · A$'], ['NZD', 'NZD'],
  ['JPY', 'JPY · ¥'], ['CNY', 'CNY'], ['SEK', 'SEK · kr'], ['NOK', 'NOK · kr'],
  ['DKK', 'DKK · kr'], ['PLN', 'PLN · zł'], ['CZK', 'CZK · Kč'], ['HUF', 'HUF · Ft'],
  ['RON', 'RON · lei'], ['BGN', 'BGN'], ['ISK', 'ISK'], ['AED', 'AED'], ['SAR', 'SAR'],
  ['QAR', 'QAR'], ['KWD', 'KWD'], ['BHD', 'BHD'], ['OMR', 'OMR'], ['ILS', 'ILS · ₪'],
  ['EGP', 'EGP'], ['MAD', 'MAD'], ['ZAR', 'ZAR'], ['INR', 'INR · ₹'], ['SGD', 'SGD'],
  ['HKD', 'HKD'], ['MXN', 'MXN'], ['BRL', 'BRL · R$'], ['AZN', 'AZN · ₼'], ['GEL', 'GEL · ₾'],
  ['KZT', 'KZT · ₸'], ['UAH', 'UAH · ₴'], ['RSD', 'RSD'],
];
